import { useState, useEffect, useRef, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useAuth0 } from '@auth0/auth0-react';
import { Box, IconButton, Typography } from '@mui/material';
import { RemoveCircleOutlined as DeleteIcon } from '@mui/icons-material';

import { FHInputField, FHSelect } from '../../../../../../../base';
import { useTreatmentPlanEditable } from '../../../../../hooks/useTreatmentPlanEditable';
import { maladaptiveSchema } from '../../../../utils/validationSchema';
import {
  validateSync,
  getAllInvalidFieldNames,
} from '../../../../../../../../helpers/validation-helpers';
import { maladaptiveBehaviorActions } from '../../../../../../../../store/treatment-plan/maladaptiveBehavior.slice';
import { debounce } from '../../../../../../../../helpers';
import { MINIMUM_REQUIRED_SENTENCE_LENGTH, Themes } from '../../../../../../../../constants';
import { FORTA_ERROR, FORTA_BLUE } from '../../../../../../../../utils/colors';
import { SYMPTOM_OPTIONS } from '../../../../constants/general';
import { ERROR_STATES, PERSIST_DATA_STATES, STATE_IDS } from '../../../../constants/states';
import { assessmentActions } from '../../../../../../../../store';
import { TreatmentPlanResourceVersion } from '../../../../../constants/general';
import ErrorAlertIcon from '../../../../../../../../assets/images/error-alert.svg';

import type { IMaladaptiveBehavior, SelectValueType } from '../../../../../../../../utils/types';

interface MaladaptiveBehaviorProps {
  maladaptiveBehavior?: IMaladaptiveBehavior;
  removeNewMaladaptivebehavior?: () => void;
}

function MaladaptiveBehavior({
  maladaptiveBehavior,
  removeNewMaladaptivebehavior,
}: MaladaptiveBehaviorProps) {
  const dispatch = useDispatch();
  const { getAccessTokenSilently } = useAuth0();
  const ref = useRef<HTMLDivElement | null>(null);

  const { current: treatmentPlan } = useSelector((state: any) => state.treatmentPlans);
  const { list: maladaptiveBehaviorList } = useSelector((state: any) => state.maladaptiveBehavior);
  const { persistDataState } = useSelector((state: any) => state.assessments);
  const { id: planId } = treatmentPlan;
  const assessmentVersion = treatmentPlan?.resource_versions?.assessment;
  const isResourceVersionV2 = assessmentVersion === TreatmentPlanResourceVersion.V2;

  const [symptomsRefWidth, setSymptomsRefWidth] = useState<number>();
  const [maladaptiveInfo, setMaladaptiveInfo] = useState<IMaladaptiveBehavior>({
    label: '',
    associated_dsm5_symptom: '',
    maladaptive_concerns: null,
  });
  const [errors, setErrors] = useState<any>({});
  const [hasDuplicateMaladaptiveBehavior, setHasDuplicateMaladaptiveBehavior] =
    useState<boolean>(false);

  const editable = useTreatmentPlanEditable();

  const updateErrorCount = (errors: any) => {
    const errorCount = Object.values(errors).filter((value) => value).length;
    dispatch(
      assessmentActions.updateErrorState({
        data: {
          [`${ERROR_STATES[STATE_IDS.MALADAPTIVE]}-${
            maladaptiveBehavior?.id ? maladaptiveBehavior.id : 'new'
          }`]: errorCount,
        },
      }),
    );
  };

  const updatePersistedData = (maladaptiveData: IMaladaptiveBehavior) => {
    const isValid = validateSync(maladaptiveSchema, {
      ...maladaptiveData,
      assessment_version: assessmentVersion,
    });
    const data = isValid ? null : maladaptiveData;
    dispatch(
      assessmentActions.updatePersistDataState({
        data: {
          [`${PERSIST_DATA_STATES[STATE_IDS.MALADAPTIVE]}-${
            maladaptiveBehavior?.id ? maladaptiveBehavior.id : 'new'
          }`]: data,
        },
      }),
    );
  };

  const debouncedErrorUpdater = useCallback(debounce(updateErrorCount, 500), []);
  const debouncedDataPersister = useCallback(debounce(updatePersistedData, 500), []);

  useEffect(() => {
    const persistedData =
      persistDataState[
        `${PERSIST_DATA_STATES[STATE_IDS.MALADAPTIVE]}-${
          maladaptiveBehavior?.id ? maladaptiveBehavior.id : 'new'
        }`
      ];
    if (!persistedData) {
      setErrors({});
      return;
    }

    setMaladaptiveInfo(persistedData);
    const data = {
      assessment_version: assessmentVersion,
      label: persistedData.label,
      associated_dsm5_symptom: persistedData.associated_dsm5_symptom,
      maladaptive_concerns: persistedData.maladaptive_concerns,
    };
    const errorsData = getAllInvalidFieldNames(maladaptiveSchema, data);
    setErrors(errorsData);
    // eslint-disable
  }, [planId, maladaptiveBehavior, persistDataState]);

  useEffect(() => {
    if (!maladaptiveBehavior) return;

    const persistedData =
      persistDataState[
        `${PERSIST_DATA_STATES[STATE_IDS.MALADAPTIVE]}-${
          maladaptiveBehavior?.id ? maladaptiveBehavior.id : 'new'
        }`
      ];
    if (persistedData) return;

    setMaladaptiveInfo(maladaptiveBehavior);
    // eslint-disable
  }, [maladaptiveBehavior]);

  useEffect(() => {
    debouncedErrorUpdater(errors);
    // eslint-disable
  }, [errors]);

  useEffect(() => {
    if (!ref || !ref.current) return;
    setSymptomsRefWidth((ref.current as any).offsetWidth);
  }, []);

  const handleFieldUpdate = (field: string, value: string) => {
    const updatedData = {
      ...maladaptiveInfo,
      [field]: value,
      assessment_version: assessmentVersion,
    };
    setMaladaptiveInfo(updatedData);
    if (field === 'label') {
      const hasDuplicateBehavior = maladaptiveBehaviorList.some(
        (behavior: IMaladaptiveBehavior) =>
          behavior.label.toLowerCase() === updatedData.label.toLowerCase() &&
          behavior.id !== maladaptiveInfo.id,
      );
      setHasDuplicateMaladaptiveBehavior(hasDuplicateBehavior);
    }
    debouncedDataPersister(updatedData);
    handleValidate(field, updatedData);
  };

  const handleValidate = (key: string, data: IMaladaptiveBehavior = maladaptiveInfo) => {
    if (!data) return;

    const invalidFields = getAllInvalidFieldNames(maladaptiveSchema, {
      assessment_version: assessmentVersion,
      label: data.label,
      associated_dsm5_symptom: data.associated_dsm5_symptom,
      maladaptive_concerns: data.maladaptive_concerns ?? null,
    });
    setErrors(invalidFields);
  };

  const handleCreateOrUpdateMaladaptiveBehavior = () => {
    if (hasDuplicateMaladaptiveBehavior) return;
    const isValid = validateSync(maladaptiveSchema, {
      ...maladaptiveInfo,
      assessment_version: assessmentVersion,
    });
    if (!isValid || !planId) return;

    if (!maladaptiveBehavior?.id) {
      dispatch(
        maladaptiveBehaviorActions.createMaladaptiveBehavior({
          data: maladaptiveInfo,
          getAccessTokenSilently,
          planId,
        }),
      );
    } else {
      dispatch(
        maladaptiveBehaviorActions.updateMaladaptiveBehavior({
          data: maladaptiveInfo,
          getAccessTokenSilently,
          planId,
          symptomId: maladaptiveBehavior.id,
        }),
      );
    }
  };

  const handleDeleteMaladaptiveBehavior = async () => {
    if (maladaptiveBehavior?.id) {
      await dispatch(
        maladaptiveBehaviorActions.deleteMaladaptiveBehavior({
          getAccessTokenSilently,
          symptomId: maladaptiveBehavior.id,
          planId,
        }),
      );
      dispatch(
        assessmentActions.updateErrorState({
          data: {
            [`${ERROR_STATES[STATE_IDS.MALADAPTIVE]}-${maladaptiveBehavior.id}`]: 0,
          },
        }),
      );
      dispatch(
        assessmentActions.updatePersistDataState({
          data: {
            [`${PERSIST_DATA_STATES[STATE_IDS.MALADAPTIVE]}-${maladaptiveBehavior.id}`]: null,
          },
        }),
      );
    } else {
      if (removeNewMaladaptivebehavior) {
        removeNewMaladaptivebehavior();
      }
    }
  };

  const symptomOptionsObject = SYMPTOM_OPTIONS.map((symptom) => ({
    value: symptom.label,
    label: symptom.label,
  }));

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        mb: 3,
      }}
    >
      <Box sx={{ minWidth: 50, width: '100%' }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }} mb={1}>
          {editable && (
            <IconButton
              sx={{ p: 0, mr: 1 }}
              disabled={!editable}
              onClick={handleDeleteMaladaptiveBehavior}
            >
              <DeleteIcon
                data-testid="deleteMaldaptiveBehaviour"
                sx={{ color: FORTA_BLUE.primary }}
              />
            </IconButton>
          )}
          <Typography fontWeight="bold" textAlign="left" ml={0}>
            Maladaptive behavior*
          </Typography>
          {hasDuplicateMaladaptiveBehavior && (
            <Box sx={{ display: 'flex', alignItems: 'center' }} ml={4}>
              <img
                style={{
                  width: '22px',
                  height: '22px',
                  marginRight: '8px',
                }}
                src={ErrorAlertIcon}
                alt="error-alert-icon"
              />
              <Typography color={FORTA_ERROR}>
                Attention: Maladaptive behavior already exists
              </Typography>
            </Box>
          )}
        </Box>
        <FHInputField
          data-testid="maladaptiveBehaviourTextField"
          value={maladaptiveInfo.label}
          fullWidth
          disabled={!editable}
          colorTheme={Themes.Primary}
          inputHeight={40}
          onChange={(e) => handleFieldUpdate('label', e.target.value)}
          onBlur={handleCreateOrUpdateMaladaptiveBehavior}
          error={errors['label'] || hasDuplicateMaladaptiveBehavior}
        />
      </Box>
      <Box sx={{ minWidth: 50, width: '100%' }} mt={2} ref={ref}>
        <Typography fontWeight="bold" textAlign="left">
          Respective DSM5 symptoms*
        </Typography>
        <FHSelect
          data-testid="associatedDsm5SymptomDropDown"
          sx={{
            flexGrow: 1,
            width: symptomsRefWidth ? symptomsRefWidth : 'auto',
          }}
          inputHeight={40}
          colorTheme={Themes.Primary}
          autoHeight
          popoverWidth={symptomsRefWidth}
          disabled={!editable}
          options={symptomOptionsObject}
          value={maladaptiveInfo.associated_dsm5_symptom}
          onUpdate={(value: SelectValueType) =>
            handleFieldUpdate('associated_dsm5_symptom', value as string)
          }
          onBlur={handleCreateOrUpdateMaladaptiveBehavior}
          error={errors['associated_dsm5_symptom']}
        />
      </Box>
      {isResourceVersionV2 && (
        <Box sx={{ minWidth: 50, width: '100%' }} mt={2} ref={ref}>
          <Typography fontWeight="bold" textAlign="left">
            Maladaptive concerns as reported by parents*
          </Typography>
          <FHInputField
            data-testid="maladaptiveConcernsText"
            value={maladaptiveInfo.maladaptive_concerns}
            fullWidth
            disabled={!editable}
            colorTheme={Themes.Primary}
            inputHeight={40}
            onChange={(e) => handleFieldUpdate('maladaptive_concerns', e.target.value)}
            onBlur={handleCreateOrUpdateMaladaptiveBehavior}
            error={errors['maladaptive_concerns']}
            helperText={
              errors['maladaptive_concerns']
                ? `Minimum ${MINIMUM_REQUIRED_SENTENCE_LENGTH} characters required`
                : ''
            }
          />
        </Box>
      )}
    </Box>
  );
}

export default MaladaptiveBehavior;
